import useAssignmentData from "@app/data-hooks/use-assignment-data.ts";
import useWorkPackageData from "@app/data-hooks/use-work-package-data.ts";
import { Button, DialogActions, DialogContent, DialogTitle, Stack, TextField } from "@mui/material";
import { useForm } from "@tanstack/react-form";
import { Dialog, QueryStatusRouter, Select } from "bi-lib";

interface Props {
    open: boolean;
    assignmentId: number;
    workPackageId?: number;
    onClose: () => void;
}

interface FormProps {
    phaseId?: number;
    packageCode?: string | null;
    packageDescription?: string | null;
    sortBy?: number | null;
}

export default function AssignmentWorkPackageModal({ open, workPackageId, onClose, assignmentId }: Props) {
    const { workPackage, createWorkPackage, updateWorkPackage } = useWorkPackageData({
        packageId: open ? workPackageId : undefined,
    });
    const { assignmentPhaseList } = useAssignmentData({ assignmentIds: assignmentId.toString() });

    const form = useForm({
        defaultValues: {
            phaseId: workPackage.data?.assignmentPhaseId,
            packageCode: workPackage.data?.workPackageCode,
            packageDescription: workPackage.data?.workPackageDescription,
        } as FormProps,
        onSubmit: async ({ value }) => {
            if (workPackageId != undefined) {
                await updateWorkPackage.mutateAsync({
                    workPackageId,
                    packageCode: value.packageCode,
                    packageDescription: value.packageDescription,
                });
            } else {
                await createWorkPackage.mutateAsync({
                    assignmentPhaseId: value.phaseId,
                    packageCode: value.packageCode,
                    packageDescription: value.packageDescription,
                });
            }
            onClose();
        },
    });

    return (
        <Dialog open={open} onClose={onClose} maxWidth={"sm"} fullWidth>
            <DialogTitle>{workPackageId == undefined ? "Create" : "Edit"} Work Package</DialogTitle>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    void form.handleSubmit();
                }}
            >
                <DialogContent>
                    <Stack spacing={1}>
                        <QueryStatusRouter queryHook={assignmentPhaseList}>
                            <form.Field name={"phaseId"}>
                                {(field) => (
                                    <Select
                                        label={"Work Phase"}
                                        value={field.state.value}
                                        onChange={(e) => {
                                            field.handleChange(e);
                                        }}
                                        disabled={workPackageId != undefined}
                                        options={assignmentPhaseList.data?.map((x) => ({
                                            label: x.displayName,
                                            value: x.assignmentPhaseId,
                                        }))}
                                    />
                                )}
                            </form.Field>
                        </QueryStatusRouter>
                        <form.Field name={"packageCode"}>
                            {(field) => (
                                <TextField
                                    label={"Work Package Code"}
                                    value={field.state.value ?? ""}
                                    onChange={(e) => field.handleChange(e.target.value)}
                                />
                            )}
                        </form.Field>
                        <form.Field name={"packageDescription"}>
                            {(field) => (
                                <TextField
                                    label={"Work Package Description"}
                                    value={field.state.value ?? ""}
                                    onChange={(e) => field.handleChange(e.target.value)}
                                    multiline
                                    minRows={2}
                                />
                            )}
                        </form.Field>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button variant={"outlined"} onClick={onClose}>
                        Close
                    </Button>
                    <Button type={"submit"} variant={"contained"}>
                        Save
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}
